import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// tailwind-merge does not read tailwind.config.ts, so every custom utility whose
// name collides with a stock class-group prefix is misclassified and can evict a
// correct class. Declaring them here is the fix for that whole class of bug.
//   bg-300%       -> backgroundSize (was parsed as a bg-COLOUR, evicting bg-gradient-to-r)
//   text-<scale>  -> fontSize       (parsed as a text-COLOUR; latent, see #3993)
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "bg-size": [{ bg: ["300%"] }],
      "font-size": [
        { text: ["report-title", "section-heading", "body", "table-meta"] },
      ],
      // Control-height tokens (tailwind.config.ts theme.extend.height/minHeight).
      // Without these, tailwind-merge cannot classify `h-ctrl*`, so a caller's own
      // height class does not evict the one buttonClasses() emits — both survive.
      "h": [{ h: ["ctrl", "ctrl-sm", "ctrl-lg"] }],
      "min-h": [{ "min-h": ["ctrl", "ctrl-sm", "ctrl-lg"] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Format a date string to 24-hour time format (HH:mm)
 */
export function formatTime24h(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Format a date string with both date and time in 24-hour format
 * Shows "Today" or "Yesterday" for recent dates, otherwise shows full date
 */
export function formatDateTime24h(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  let datePrefix: string;
  if (dateOnly.getTime() === today.getTime()) {
    datePrefix = 'Today';
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    datePrefix = 'Yesterday';
  } else {
    datePrefix = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  }
  
  const time = formatTime24h(date);
  return `${datePrefix}, ${time}`;
}

/**
 * Check if text contains significant Hebrew content (>30% Hebrew characters)
 * Returns true if the text should be displayed as RTL Hebrew
 */
export function containsHebrew(text: string): boolean {
  if (!text) return false;
  // Count Hebrew characters (Unicode range U+0590 to U+05FF)
  const hebrewChars = text.match(/[\u0590-\u05FF]/g) || [];
  // Count all alphanumeric characters (excluding spaces, punctuation)
  const alphaChars = text.match(/[a-zA-Z\u0590-\u05FF]/g) || [];
  if (alphaChars.length === 0) return false;
  const ratio = hebrewChars.length / alphaChars.length;
  return ratio > 0.3;
}

/**
 * Get text direction based on content
 * Returns 'rtl' for Hebrew text, 'ltr' otherwise
 */
export function getTextDirection(text: string): 'rtl' | 'ltr' {
  return containsHebrew(text) ? 'rtl' : 'ltr';
}

/**
 * Decode URL-encoded slug to display readable text
 * Handles Hebrew and other non-ASCII characters that are URL-encoded in WordPress slugs
 * @param slug - The URL-encoded slug string
 * @returns Decoded human-readable slug
 */
export function decodeSlug(slug: string | null | undefined): string {
  if (!slug) return '';
  try {
    return decodeURIComponent(slug);
  } catch (e) {
    // If decoding fails, return original slug
    return slug;
  }
}
/**
 * Decodes WordPress-specific link entities like &#038;
 * @param url - The URL string to decode
 * @returns Decoded URL string
 */
export function decodeWordPressLink(url: string | null | undefined): string {
  if (!url) return '';
  return url.replace(/&#038;/g, '&');
}

/**
 * Removes Unicode bidi and format control characters that should never render visibly.
 * Covers: LRM/RLM (U+200E, U+200F), LRE/RLE/PDF/LRO/RLO (U+202A–U+202E),
 * LRI/RLI/FSI/PDI (U+2066–U+2069). These chars are sometimes injected into
 * user-supplied display strings (e.g. via OAuth display names or manual edits)
 * and render as visible glyphs in some fonts when they should be invisible.
 *
 * Returns '' for null/undefined input so the helper is safe at any interpolation site.
 */
export function stripUnicodeControls(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '');
}
